import { useState, useEffect } from 'react';

// MUI Components
import {
  TextField,
  FormControl,
  Box,
  Typography,
  Autocomplete,
  Button,
  CircularProgress
} from '@mui/material';

// lucide Icons
import {
  BadgeDollarSign,
  ShieldCheck,
  Package2,
  Ruler,
  StickyNote,
  Box as BoxIcon,
  Wand2
} from 'lucide-react';

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { fetchWrapperNoAuth, SERVER } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import { set } from 'lodash';

const knownSources = [
  "Walmart",
  "Ollies",
  "Ross",
  "Amazon",
  "Target",
  "Cargo Largo",
  "Dollar General",
  "Lee/Wrangler",
  "BB"
];


export default function ProductForm({ product, onChange }) {
  const { theme } = useTheme();
  const { user, password } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [alreadyFetched, setAlreadyFetched] = useState(false);


  const fetchDimensionsAndWeight = async (sku) => {
    setIsLoading(true);
    const res = await fetchWrapperNoAuth(`${SERVER}/fetch_metadata/sku/${encodeURIComponent(sku)}`, user, password).catch(err => {
      setAlreadyFetched(true);
      setIsLoading(false);
      return;
    })
    onChange('weight', res?.weight > 0 ? parseFloat(res?.weight).toFixed(2) : null)
    onChange('length', res?.dimensions?.length > 0 ? parseFloat(res?.dimensions?.length).toFixed(2) : null)
    onChange('width', res?.dimensions?.width > 0 ? parseFloat(res?.dimensions?.width).toFixed(2) : null)
    onChange('height', res?.dimensions?.height > 0 ? parseFloat(res?.dimensions?.height).toFixed(2) : null)
    setAlreadyFetched(true);
    setIsLoading(false);
  }

  useEffect(() => {
    setAlreadyFetched(false);
    setIsLoading(false);
  }, [product.sku])

  const inputSx = {
    mb: 2,
    bgcolor: theme.background.secondary,
    borderRadius: 1.5,
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: theme.color.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.color.primary,
      }
    },
    "& .MuiInputLabel-root": {
      color: theme.text.primary,
      fontFamily: theme.fontFamily,
      "&.Mui-focused": {
        color: theme.text.primary,
      },
    },
    "& .MuiOutlinedInput-input": {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
    },
  };

  return (
    <>
      <Typography variant="h6" color={theme.text.primary} fontWeight={600} mb={1} sx={{ fontSize: '1.1rem', fontFamily: theme.fontFamily }}>
        {product.name || product.product_name}
      </Typography>
      <Typography variant="subtitle2" color={theme.text.secondary} mb={2}
        sx={{
          display: 'inline-block',
          px: 1,
          py: 0.5,
          bgcolor: `${theme.color.secondary}15`,
          borderRadius: 1,
          fontSize: '0.8rem',
          fontFamily: theme.fontFamily
        }}>
        SKU: <strong>{product.sku}</strong>
      </Typography>

      <FormControl fullWidth sx={inputSx}>
        <TextField
          label="Cost ($) *"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={product.cost || ""}
          onChange={(e) => onChange('cost', parseFloat(e.target.value) || 0)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <BadgeDollarSign size={18} style={{ marginRight: 8, color: theme.text.tertiary }} />,
            sx: { '&:focus-within .MuiSvgIcon-root': { color: theme.color.primary } }
          }}
        />
      </FormControl>

      <FormControl fullWidth sx={inputSx}>
        <Autocomplete
          freeSolo
          options={knownSources}
          value={product.source || ''}
          onChange={(_, newValue) => onChange('source', newValue || '')}
          inputValue={product.source || ''}
          onInputChange={(_, newInputValue) => onChange('source', newInputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Source *"
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <ShieldCheck size={18} style={{ marginRight: 8, color: theme.text.tertiary, flexShrink: 0 }} />
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box key={option} component="li" {...props}>
              <Box>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily }}>
                  {option}
                </Typography>
              </Box>
            </Box>
          )}
        />
      </FormControl>

      <FormControl fullWidth sx={inputSx}>
        <TextField
          label="Quantity *"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={product.qty || ""}
          onChange={(e) => onChange('qty', parseInt(e.target.value) || 0)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <Package2 size={18} style={{ marginRight: 8, color: theme.text.tertiary }} />
          }}
        />
      </FormControl>
      {!(product?.weight && product?.length && product?.width && product?.height) && <Box sx={{ mb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          disabled={isLoading || alreadyFetched}
          onClick={() => {
            fetchDimensionsAndWeight(product.sku);
          }}
          sx={{
            py: 1,
            fontWeight: 600,
            fontFamily: theme.fontFamily,
            borderRadius: 1.5,
            borderWidth: 1.5,
            transition: 'all 0.2s ease-in-out',
            bgcolor: `${theme.color.secondary}10`,
            '&:hover': {
              borderWidth: 1.5,
              transform: 'translateY(-1px)',
              bgcolor: `${theme.color.secondary}20`,
              boxShadow: '0 3px 8px rgba(0,0,0,0.08)'
            },
            '&.Mui-disabled': {
              bgcolor: `${theme.color.secondary}05`,
              borderColor: `${theme.color.secondary}40`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLoading ? (
              <CircularProgress
                size={18}
                sx={{
                  color: theme.color.secondary,
                  textTransform: "none",
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
            ) : (
              <Wand2 size={18} />
            )}
            {isLoading ? 'Getting Product Details...' : alreadyFetched ? "Not All Details Found" : 'Load Product Details'}
          </Box>
        </Button>
      </Box>}
      {['length', 'width', 'height', 'weight'].map(field => (
        <FormControl key={field} fullWidth sx={inputSx}>
          <TextField
            label={`${field.charAt(0).toUpperCase() + field.slice(1)} ${field === 'weight' ? '(lbs)' : '(in)'} *`}
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            value={product[field] || ""}
            onChange={(e) => onChange(field, parseFloat(e.target.value) || 0)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <Ruler size={18} style={{ marginRight: 8, color: theme.text.tertiary }} />,
              sx: { '&:focus-within .MuiSvgIcon-root': { color: theme.color.primary } }
            }}
          />
        </FormControl>
      ))}

      <FormControl fullWidth sx={inputSx}>
        <TextField
          label="Inner Pack"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={product.inner_pack || ""}
          onChange={(e) => onChange('inner_pack', parseInt(e.target.value) || 0)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <BoxIcon size={18} style={{ marginRight: 8, color: theme.text.tertiary }} />,
            sx: { '&:focus-within .MuiSvgIcon-root': { color: theme.color.primary } }
          }}
        />
      </FormControl>


      <FormControl fullWidth sx={inputSx}>
        <TextField
          label="Additional Cost ($)"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={product.additional_costs || ""}
          onChange={(e) => onChange('additional_costs', parseFloat(e.target.value) || 0)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <BadgeDollarSign size={18} style={{ marginRight: 8, color: theme.text.tertiary }} />,
            sx: { '&:focus-within .MuiSvgIcon-root': { color: theme.color.primary } }
          }}
        />
      </FormControl>

      <FormControl fullWidth sx={inputSx}>
        <TextField
          label="Purchase Date"
          type="date"
          value={product.purchase_date || ''}
          onChange={(e) => onChange('purchase_date', e.target.value)}
          variant="outlined"
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </FormControl>

      <FormControl fullWidth sx={inputSx}>
        <TextField
          label="Notes"
          multiline
          rows={2}
          value={product.notes || ''}
          onChange={(e) => onChange('notes', e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <StickyNote size={18} style={{ marginRight: 8, color: theme.text.tertiary }} />,
            sx: { '&:focus-within .MuiSvgIcon-root': { color: theme.color.primary } }
          }}
        />
      </FormControl>
    </>
  );
}
